import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getDecodedFileName } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import FileUploader from '@/components/global/FileUploader';
import { getClientFiles, uploadClientFile, deleteClientFile, getClientFileDownloadUrl, getUser, changeClientVerificationStatus } from '@/service/weexpertService';
import { toast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';

function SendForReviewButton({clientData, showButton}) {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const handleClick = () => {
    changeClientVerificationStatus(clientData.data.id, "ASKED_FOR_VERIFICATION")
    .then(() => {
      navigate('/');
    })
    .catch((err) => {
      console.log(err)
      toast({
        title: t('documents:error_title'),
        variant: "destructive",
      });
   })
  };
  console.log(clientData.data.verificationState)
  const message = clientData.data.verificationState === "VERIFIED" ? 'OK' : t('documents:review_send_success');
  return (
    <Button
      onClick={handleClick}
      disabled={!showButton}
      variant="outline"
       className={`p-4 h-full  flex flex-col items-start justify-center whitespace-normal break-words font-medium w-full text-center
        border-[1px] transition-all
        ${!showButton
          ? "border-green-400 bg-green-50 text-green-600 disabled:opacity-100"
          : "border-blue-300 text-blue-500 hover:bg-blue-50 hover:border-blue-600"
        }`}
    >
      <div className="font-medium w-full text-center">
        {!showButton ? message : t('documents:review_send')}
      </div>
    </Button>
  );
}

// Document types enum - matches backend ClientUploadFileType
const DOCUMENT_TYPES = {
  PASSPORT: 'Passport',
  KARTA_POBYTU: 'Karta Pobytu',
  DECYZJA: 'Decyzja',
  VISA: 'Visa',
  CERTYFIKAT_REZYDENCJI_PODATKOWEJ: 'Certyfikat rezydencji podatkowej'
};

// Document type display names - now using translations
const getDocumentLabel = (type, t) => {
  const labels = {
    [DOCUMENT_TYPES.PASSPORT]: t('documents:types_passport'),
    [DOCUMENT_TYPES.KARTA_POBYTU]: t('documents:types_karta_pobytu'),
    [DOCUMENT_TYPES.DECYZJA]: t('documents:types_decyzja'),
    [DOCUMENT_TYPES.VISA]: t('documents:types_visa'),
    [DOCUMENT_TYPES.CERTYFIKAT_REZYDENCJI_PODATKOWEJ]: t('documents:types_certyfikat_rezydencji_podatkowej')
  };
  return labels[type] || type;
};

export const DocumentsTab = ({ userData }) => {

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingType, setUploadingType] = useState(null);

  // Check if user is Polish citizen
  const isPolishCitizen = userData?.citizenship_code === 'pl';

  // Get client files
  const { data: filesData, isLoading: filesLoading } = useQuery({
    queryKey: ['clientFiles'],
    queryFn: () => getClientFiles(),
    refetchOnWindowFocus: false
  });

  const { data: clientData, isLoading: isClientLoading} = useQuery({
    queryKey: ['userMe'],
    queryFn: () => getUser(),
    refetchOnWindowFocus: false
  });

  // Upload file mutation
  const uploadFileMutation = useMutation({
    mutationFn: async ({ fileType, file }) => {
      return uploadClientFile(fileType, file);
    },
    onSuccess: () => {
      toast({
        title: t('documents:success_title'),
        description: t('documents:success_upload'),
      });
      setSelectedDocumentType(null);
      setSelectedFiles([]);
      setUploadingType(null);
      queryClient.invalidateQueries(['clientFiles']);
    },
    onError: (error) => {
      let errorTitle = t('documents:error_title');
      let errorDescription = t('documents:error_upload');

      // Check if error has response data
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;

        // Check for specific error types
        if (serverMessage.includes("Client not found")) {
          errorTitle = t('documents:error_client_not_found_title');
          errorDescription = t('documents:error_client_not_found_description');
        } else if (serverMessage.includes("Invalid file type")) {
          errorTitle = t('documents:error_invalid_file_type_title');
          errorDescription = t('documents:error_invalid_file_type_description');
        } else {
          // For other errors, show the actual message
          errorDescription = serverMessage;
        }
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      });
    }
  });

  // Delete file mutation
  const deleteFileMutation = useMutation({
    mutationFn: deleteClientFile,
    onSuccess: () => {
      toast({
        title: t('documents:success_title'),
        description: t('documents:success_delete'),
      });
      queryClient.invalidateQueries(['clientFiles']);
    },
    onError: (error) => {
      toast({
        title: t('documents:error_title'),
        description: error.response?.data?.message || t('documents:error_delete'),
        variant: "destructive",
      });
    }
  });

  const handleFileUpload = (fileType) => {
    if (!selectedFiles.length) {
      toast({
        title: t('documents:error_title'),
        description: t('documents:error_select_file'),
        variant: "destructive",
      });
      return;
    }

    setUploadingType(fileType);
    uploadFileMutation.mutate({
      fileType: fileType,
      file: selectedFiles[0] // Only upload the first file
    });
  };

  const handleFileDelete = (fileId) => {
    if (window.confirm(t('documents:confirm_delete'))) {
      deleteFileMutation.mutate(fileId);
    }
  };

  const handleFileDownload = async (fileId, fileName) => {
    try {
      const response = await getClientFileDownloadUrl(fileId);
      const downloadUrl = response.data.url;

      // Create a temporary link and click it to download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast({
        title: t('documents:error_title'),
        description: t('documents:error_download'),
        variant: "destructive",
      });
    }
  };

  const hasFeePayment = userData?.features?.includes('FEE_PAYMENT');
  const files = Array.isArray(filesData?.data?.data) ? filesData.data.data : [];

  // Group files by type
  const filesByType = files.reduce((acc, file) => {
    if (!acc[file.type]) {
      acc[file.type] = [];
    }
    acc[file.type].push(file);
    return acc;
  }, {});

  if (filesLoading) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">{t('documents:loading')}</div>
      </div>
    );
  }

  if (!hasFeePayment) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">
          {t('documents:feature_not_available')}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3>{t('documents:title')}</h3>

      {/* Upload New Document Section */}
      {!selectedDocumentType && (
       <div>
          <div className="text-lg font-medium mb-4">
            {t('documents:upload_new_document')}
          </div>

          {isPolishCitizen && (
            <Alert className="mb-4">
              <AlertDescription>
                {t('documents:polish_citizen_message')}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
            {Object.entries(DOCUMENT_TYPES)
              .filter(([key, value]) => !isPolishCitizen || value === DOCUMENT_TYPES.CERTYFIKAT_REZYDENCJI_PODATKOWEJ)
              .map(([key, value]) => (
                <Button
                  key={key}
                  onClick={() => setSelectedDocumentType(value)}
                  variant="outline"
                  className="p-4 h-full w-full text-left flex flex-col items-start justify-between whitespace-normal break-words"
                >
                  <div className="font-medium w-full break-words">
                    {getDocumentLabel(value, t)}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {filesByType[value]?.length || 0} {t('documents:files_uploaded')}
                  </div>
                </Button>
              ))}

            {!isClientLoading && clientData && clientData.data && (
              <SendForReviewButton clientData={clientData} showButton={
                !["ASKED_FOR_VERIFICATION", "VERIFIED"].includes(clientData.data.verificationState)
              } />)
            }
          </div>
        </div>
      )}

      {/* Upload Form */}
      {selectedDocumentType && (
        <div className="space-y-4">
          <div className="font-medium mb-4">
            {t('documents:upload_title')}: {getDocumentLabel(selectedDocumentType, t)}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('documents:select_file')}</label>
            <FileUploader
              onChange={setSelectedFiles}
              maxFiles={1}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              onClick={() => {
                setSelectedDocumentType(null);
                setSelectedFiles([]);
                setUploadingType(null);
              }}
              disabled={uploadFileMutation.isLoading}
              variant="outline"
            >
              {t('documents:button_cancel')}
            </Button>
            {selectedFiles.length > 0 && (
              <Button
                onClick={() => handleFileUpload(selectedDocumentType)}
                disabled={uploadFileMutation.isLoading}
              >
                {uploadingType === selectedDocumentType ? t('documents:button_uploading') : t('documents:button_upload')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Existing Documents */}
      {Object.entries(DOCUMENT_TYPES)
        .filter(([key, value]) => !isPolishCitizen || value === DOCUMENT_TYPES.CERTYFIKAT_REZYDENCJI_PODATKOWEJ)
        .map(([key, documentType]) => {
        const typeFiles = filesByType[documentType] || [];

        if (typeFiles.length === 0) return null;

        return (
          <div key={key} className="border rounded-lg p-4">
            <div className="text-lg font-medium mb-4">{getDocumentLabel(documentType, t)}</div>
            <div className="space-y-3">
              {typeFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium">{getDecodedFileName(file.file?.path) || t('documents:document_label')}</div>
                    <div className="text-sm text-gray-500">
                      {t('documents:uploaded_at')}: {format(new Date(file.createdAt), 'dd.MM.yyyy HH:mm')}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleFileDownload(file.id, getDecodedFileName(file.file?.path))}
                    >
                      {t('documents:button_download')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleFileDelete(file.id)}
                      disabled={deleteFileMutation.isLoading}
                    >
                      {t('documents:button_delete')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {files.length === 0 && !selectedDocumentType && (
        <Alert>
          <AlertDescription>
            {t('documents:no_documents_message')}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
